import {
  PublicClientApplication,
  Configuration,
  SilentRequest,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  type PopupRequest
} from "@azure/msal-browser";
import { environment } from "../environments/environment";

// ============================================================================
// MSAL Configuration
// ============================================================================

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.azureClientId,
    authority: `https://login.microsoftonline.com/${environment.azureTenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin + "/api/logout"
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false
  }
};

export const loginRequest: Pick<PopupRequest, "scopes"> = {
  scopes: [`${environment.azureClientId}/.default`]
};

// ============================================================================
// MSAL Instance & Initialization
// ============================================================================

export const msalInstance = new PublicClientApplication(msalConfig);

class MsalInitializationManager {
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = msalInstance
      .initialize()
      .then(() => {
        this.initialized = true;
      })
      .catch((error) => {
        // Reset on error to allow retry
        this.initializationPromise = null;
        throw error;
      });

    return this.initializationPromise;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

const initManager = new MsalInitializationManager();

/**
 * Initialize the MSAL instance (safe to call multiple times)
 */
export const initializeMsal = (): Promise<void> => {
  return initManager.initialize();
};

// ============================================================================
// Token Acquisition
// ============================================================================

const getActiveAccount = (): AccountInfo | null => {
  return msalInstance.getActiveAccount();
};

const acquireTokenSilently = async (account: AccountInfo): Promise<string | null> => {
  try {
    const silentRequest: SilentRequest = {
      scopes: loginRequest.scopes,
      account
    };

    const response: AuthenticationResult = await msalInstance.acquireTokenSilent(
      silentRequest
    );

    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // This is expected when token refresh fails
      return null;
    }

    console.error("Silent token acquisition failed:", error);

    return null;
  }
};

const acquireTokenWithPopup = async (account: AccountInfo): Promise<string | null> => {
  try {
    const silentRequest: SilentRequest = {
      scopes: loginRequest.scopes,
      account
    };

    const response: AuthenticationResult = await msalInstance.acquireTokenPopup(
      silentRequest
    );

    return response.accessToken;
  } catch (error) {
    console.error("Interactive token acquisition failed:", error);

    return null;
  }
};

/**
 * Get an access token for the current user
 * Safe to call from anywhere (GraphQL, fetch, etc.)
 *
 * Tries silent acquisition first, falls back to popup if needed
 */
export const getToken = async (): Promise<string> => {
  try {
    const account = getActiveAccount();

    if (!account) {
      return "";
    }

    const silentToken = await acquireTokenSilently(account);

    if (silentToken) {
      return silentToken;
    }

    const popupToken = await acquireTokenWithPopup(account);

    return popupToken || "";
  } catch (error) {
    console.error("Token acquisition failed:", error);

    return "";
  }
};
